import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Plus, Trash2, Shield, UserCog, Edit, Archive, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/constants';

const emptyUser = { email: '', password: '', name: '', role: '', location_id: '', phone: '' };
const emptyRole = { code: '', label: '', permissions: [] };

const UsersPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [userForm, setUserForm] = useState(emptyUser);
  const [roleForm, setRoleForm] = useState(emptyRole);
  const [editingUserId, setEditingUserId] = useState(null); // null = create, string = edit
  const [editingRoleCode, setEditingRoleCode] = useState(null); // null = create, string = edit
  const [editingRoleIsBuiltin, setEditingRoleIsBuiltin] = useState(false);

  const openEditRole = (r) => {
    setEditingRoleCode(r.code);
    setEditingRoleIsBuiltin(!!r.builtin);
    // For super_admin builtin with '*' permissions, show all as checked to allow customization
    let perms = r.permissions || [];
    if (perms.includes('*')) {
      perms = allPermissions.map((p) => p.code);
    }
    setRoleForm({ code: r.code, label: r.label, permissions: perms });
    setShowRoleDialog(true);
  };

  const openCreateRole = () => {
    setEditingRoleCode(null);
    setEditingRoleIsBuiltin(false);
    setRoleForm(emptyRole);
    setShowRoleDialog(true);
  };

  const handleResetBuiltinRole = async (r) => {
    if (!window.confirm(`Reset permission "${r.label}" ke default bawaan?`)) return;
    try {
      await api.post(`/roles/${r.code}/reset`);
      toast.success('Permission direset ke default');
      setShowRoleDialog(false);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset');
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [usersResp, locsResp, rolesResp] = await Promise.all([
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/locations'),
        api.get('/roles'),
      ]);
      setUsers(usersResp.data);
      setLocations(locsResp.data);
      setRoles(rolesResp.data.roles || []);
      setAllPermissions(rolesResp.data.permissions || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditUser = (u) => {
    setEditingUserId(u.id);
    setUserForm({
      email: u.email || '',
      password: '',
      name: u.name || '',
      role: u.role || '',
      location_id: u.location_id || '',
      phone: u.phone || '',
    });
    setShowUserDialog(true);
  };

  const openCreateUser = () => {
    setEditingUserId(null);
    setUserForm(emptyUser);
    setShowUserDialog(true);
  };

  const handleCreateUser = async () => {
    try {
      if (editingUserId) {
        if (userForm.password && userForm.password.trim().length > 0 && userForm.password.trim().length < 6) {
          toast.error('Password minimum 6 karakter');
          return;
        }
        const payload = {
          name: userForm.name,
          phone: userForm.phone,
          role: userForm.role,
          location_id: userForm.location_id,
        };
        if (userForm.password && userForm.password.trim()) {
          payload.password = userForm.password.trim();
        }
        await api.put(`/users/${editingUserId}`, payload);
        toast.success('User berhasil diperbarui');
      } else {
        await api.post('/auth/register', userForm);
        toast.success('User berhasil ditambahkan');
      }
      setShowUserDialog(false);
      setUserForm(emptyUser);
      setEditingUserId(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingUserId ? 'Gagal memperbarui user' : 'Gagal menambahkan user'));
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Hapus user "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('User berhasil dihapus');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    }
  };

  const togglePermission = (code) => {
    setRoleForm((f) => ({
      ...f,
      permissions: f.permissions.includes(code)
        ? f.permissions.filter((p) => p !== code)
        : [...f.permissions, code],
    }));
  };

  const handleCreateRole = async () => {
    if (!roleForm.code.trim() || !roleForm.label.trim()) {
      toast.error('Kode dan nama role wajib diisi');
      return;
    }
    try {
      if (editingRoleCode) {
        // Update existing custom role (label + permissions)
        await api.put(`/roles/${editingRoleCode}`, {
          label: roleForm.label,
          permissions: roleForm.permissions,
        });
        toast.success('Role berhasil diperbarui');
      } else {
        await api.post('/roles', roleForm);
        toast.success('Role berhasil dibuat');
      }
      setShowRoleDialog(false);
      setRoleForm(emptyRole);
      setEditingRoleCode(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan role');
    }
  };

  const handleDeleteRole = async (role) => {
    const msg = role.builtin
      ? `Sembunyikan role bawaan "${role.label}"? Bisa direstore nanti dari tombol 'Role Tersembunyi'.`
      : `Hapus role "${role.label}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/roles/${role.code}`);
      toast.success(role.builtin ? 'Role disembunyikan' : 'Role berhasil dihapus');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus role');
    }
  };

  const [hiddenRoles, setHiddenRoles] = useState([]);
  const [showHiddenDialog, setShowHiddenDialog] = useState(false);

  const openHiddenDialog = async () => {
    try {
      const { data } = await api.get('/roles/hidden');
      setHiddenRoles(data);
      setShowHiddenDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat role tersembunyi');
    }
  };

  const handleRestoreRole = async (r) => {
    try {
      await api.post(`/roles/${r.code}/restore`);
      toast.success(`Role "${r.label}" direstore`);
      setShowHiddenDialog(false);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal restore');
    }
  };

  const isSuperAdmin = me?.role === 'super_admin';

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Manajemen</p>
            <h1 className="text-4xl font-medium tracking-tight">User &amp; Role</h1>
            <p className="text-muted-foreground mt-1">Kelola user dan hak akses sistem</p>
          </div>
          {isSuperAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={openHiddenDialog}
                data-testid="hidden-roles-button"
              >
                <Archive className="w-4 h-4 mr-2" />
                Role Tersembunyi
              </Button>
              <Button
                variant="outline"
                onClick={openCreateRole}
                data-testid="add-role-button"
              >
                <Shield className="w-4 h-4 mr-2" />
                Buat Role
              </Button>
              <Button
                onClick={openCreateUser}
                className="bg-primary hover:bg-primary/90"
                data-testid="add-user-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah User
              </Button>
            </div>
          )}
        </div>

        {/* Daftar User */}
        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl tracking-tight font-medium flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Daftar User ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Belum ada user (login sebagai super_admin untuk mengelola)</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 border-b border-border">
                    <tr>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Nama</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Email</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden lg:table-cell">No. WhatsApp</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Role</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Cabang</th>
                      <th className="text-right px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((u) => {
                      const roleLabel = roles.find((r) => r.code === u.role)?.label || u.role;
                      return (
                        <tr key={u.id} className="hover:bg-secondary/30" data-testid={`user-row-${u.id}`}>
                          <td className="px-4 sm:px-6 py-3">
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-muted-foreground sm:hidden">{u.email}</p>
                          </td>
                          <td className="px-4 sm:px-6 py-3 text-muted-foreground hidden sm:table-cell">{u.email}</td>
                          <td className="px-4 sm:px-6 py-3 text-muted-foreground hidden lg:table-cell">{u.phone || '—'}</td>
                          <td className="px-4 sm:px-6 py-3">
                            <Badge variant="outline" className="text-xs">{roleLabel}</Badge>
                          </td>
                          <td className="px-4 sm:px-6 py-3 hidden md:table-cell">{u.location_name || '—'}</td>
                          <td className="px-4 sm:px-6 py-3 text-right">
                            {isSuperAdmin ? (
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditUser(u)}
                                  className="h-7 w-7 p-0"
                                  data-testid={`user-edit-${u.id}`}
                                  title="Edit user"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                                {u.id !== me?.id ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteUser(u)}
                                    className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    data-testid={`user-delete-${u.id}`}
                                    title="Hapus user"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                ) : (
                                  <Badge variant="secondary" className="text-xs ml-1">Anda</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Sistem */}
        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl tracking-tight font-medium flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Role Sistem ({roles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((r) => (
                <div key={r.code} className="p-4 rounded-lg bg-secondary/50 border border-border" data-testid={`role-card-${r.code}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{r.label}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-1">{r.code}</p>
                    </div>
                    {r.builtin ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant="secondary" className="text-[10px]">
                          {r.customized ? 'Bawaan • diubah' : 'Bawaan'}
                        </Badge>
                        {isSuperAdmin && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditRole(r)}
                              className="h-6 w-6 p-0"
                              data-testid={`role-edit-${r.code}`}
                              title="Edit permission role bawaan"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            {r.code !== 'super_admin' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteRole(r)}
                                className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                data-testid={`role-delete-${r.code}`}
                                title="Sembunyikan role bawaan"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      isSuperAdmin && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditRole(r)}
                            className="h-6 w-6 p-0"
                            data-testid={`role-edit-${r.code}`}
                            title="Edit role"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRole(r)}
                            className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            data-testid={`role-delete-${r.code}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                  {r.permissions && r.permissions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Wewenang</p>
                      <div className="flex flex-wrap gap-1">
                        {r.permissions.slice(0, 4).map((p) => (
                          <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            {p === '*' ? 'Semua akses' : (allPermissions.find((ap) => ap.code === p)?.label || p)}
                          </span>
                        ))}
                        {r.permissions.length > 4 && (
                          <span className="text-[10px] text-muted-foreground">+{r.permissions.length - 4} lain</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Add / Edit User Dialog */}
        <Dialog open={showUserDialog} onOpenChange={(open) => { setShowUserDialog(open); if (!open) { setEditingUserId(null); setUserForm(emptyUser); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingUserId ? 'Edit User' : 'Tambah User Baru'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nama *</Label>
                  <Input
                    placeholder="Nama lengkap"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email {editingUserId ? '' : '*'}</Label>
                  <Input
                    type="email"
                    placeholder="user@hotel.com"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    disabled={!!editingUserId}
                    data-testid="user-email-input"
                  />
                  {editingUserId && (
                    <p className="text-xs text-muted-foreground">Email tidak dapat diubah</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {!editingUserId && (
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      placeholder="Minimum 6 karakter"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      data-testid="user-password-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>No. WhatsApp</Label>
                  <Input
                    placeholder="+6281234567890"
                    value={userForm.phone}
                    onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                    data-testid="user-phone-input"
                  />
                </div>
              </div>
              {editingUserId && (
                <div className="space-y-2">
                  <Label>Password Baru</Label>
                  <Input
                    type="password"
                    placeholder="Kosongkan jika tidak ingin mengubah"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    data-testid="user-password-input"
                  />
                  <p className="text-xs text-muted-foreground">Minimum 6 karakter. Kosongkan untuk mempertahankan password lama.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Pilih role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lokasi Cabang</Label>
                <Select value={userForm.location_id} onValueChange={(v) => setUserForm({ ...userForm, location_id: v })}>
                  <SelectTrigger data-testid="user-location-select">
                    <SelectValue placeholder="Pilih lokasi" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.filter((loc) => loc.id).map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserDialog(false)}>Batal</Button>
              <Button onClick={handleCreateUser} className="bg-primary hover:bg-primary/90" data-testid="save-user-button">
                {editingUserId ? 'Simpan Perubahan' : 'Simpan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Hidden Roles Dialog */}
        <Dialog open={showHiddenDialog} onOpenChange={setShowHiddenDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Role Bawaan Tersembunyi</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-2">
              {hiddenRoles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Tidak ada role bawaan yang disembunyikan.
                </p>
              ) : (
                hiddenRoles.map((r) => (
                  <div key={r.code} className="flex items-center justify-between p-3 rounded-md bg-secondary/50 border border-border" data-testid={`hidden-role-${r.code}`}>
                    <div>
                      <p className="font-medium">{r.label}</p>
                      <p className="text-xs text-muted-foreground font-mono">{r.code}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestoreRole(r)}
                      data-testid={`restore-role-${r.code}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Restore
                    </Button>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHiddenDialog(false)}>Tutup</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Add / Edit Role Dialog */}
        <Dialog open={showRoleDialog} onOpenChange={(open) => { setShowRoleDialog(open); if (!open) { setEditingRoleCode(null); setEditingRoleIsBuiltin(false); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRoleCode
                  ? (editingRoleIsBuiltin ? 'Edit Wewenang Role Bawaan' : 'Edit Role')
                  : 'Buat Role Baru'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nama Role *</Label>
                  <Input
                    placeholder="Supervisor Cabang"
                    value={roleForm.label}
                    onChange={(e) => setRoleForm({ ...roleForm, label: e.target.value })}
                    disabled={editingRoleIsBuiltin}
                    data-testid="role-label-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kode *</Label>
                  <Input
                    placeholder="supervisor_cabang"
                    value={roleForm.code}
                    onChange={(e) => setRoleForm({ ...roleForm, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    disabled={!!editingRoleCode}
                    data-testid="role-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Wewenang / Permission</Label>
                <div className="border border-border rounded-md p-3 space-y-2 max-h-64 overflow-y-auto bg-secondary/30">
                  {allPermissions.map((p) => (
                    <label key={p.code} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={roleForm.permissions.includes(p.code)}
                        onChange={() => togglePermission(p.code)}
                        className="rounded border-border"
                        data-testid={`perm-${p.code}`}
                      />
                      <span>{p.label}</span>
                      <span className="text-[10px] text-muted-foreground font-mono ml-auto">{p.code}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {roleForm.permissions.length} wewenang dipilih. Role custom dapat dipilih saat membuat user.
                </p>
              </div>
            </div>
            <DialogFooter>
              {editingRoleIsBuiltin && (
                <Button
                  variant="outline"
                  onClick={() => handleResetBuiltinRole({ code: editingRoleCode, label: roleForm.label })}
                  className="mr-auto"
                  data-testid="reset-role-button"
                >
                  Reset ke Default
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Batal</Button>
              <Button onClick={handleCreateRole} className="bg-primary hover:bg-primary/90" data-testid="save-role-button">
                {editingRoleCode ? 'Simpan Perubahan' : 'Simpan Role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
